import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "../../src/shared/google_maps_loader"
import MapStyle from "src/shared/resources/map-style.json"
import MapStyleLight from "src/shared/resources/map-style-light.json"
import { clusterIcon, pinIcon } from "../../src/shared/map_markers"
import { whenVisible } from "../../src/shared/when_visible"

// Loaded with the map when clustering is on, so it stays out of the bundle every page downloads.
let MarkerClusterer

const RESULT_CARD_SELECTOR = "[data-office-id] .visitor-practitioner-result-card"
// With an outline, markers farther than this from its centre stay on the map but out of the initial frame:
// one office 70 km away would shrink the area to a dot.
const AREA_FIT_DISTANCE_KM = 40
const EARTH_RADIUS_KM = 6371
// Fewer markers than this are all drawn as pins: grouping 3 offices hides one under the other's bubble (Martinique).
const CLUSTER_MIN_MARKERS = 10
// A clicked marker zooms in to street level, never out: close enough to read the streets, wide enough to keep the neighbourhood.
const MARKER_FOCUS_ZOOM = 15

function distanceInKm (from, to) {
  const toRadians = (degrees) => degrees * Math.PI / 180
  const latDelta = toRadians(to.lat - from.lat)
  const lngDelta = toRadians(to.lng - from.lng)
  const a = Math.sin(latDelta / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(lngDelta / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a))
}

export default class extends Controller {
  static targets = ["canvas", "defaultCard", "selectedCard", "closeButton"]
  static values = {
    lat: Number,
    lng: Number,
    zoom: { type: Number, default: 12 },
    theme: { type: String, default: "default" },
    radius: Number,
    cluster: { type: Boolean, default: false },
    fitBounds: { type: Boolean, default: false },
    // Steps added to the zoom fitBounds picks: its frame keeps every marker, a closer one shows the city.
    fitBoundsZoomOffset: { type: Number, default: 0 },
    // Kilometres around the centre framed at load, whatever the markers (zone page: its circle, capped). Wins over
    // fitBounds: pins lopsided on one side no longer pull the place off-centre.
    frameRadius: { type: Number, default: 0 },
    markers: { type: Array, default: [] },
    // Polygon path ([{ lat, lng }, …]) outlined on the map, e.g. a zone sector.
    area: { type: Array, default: [] },
    // With an area, frame it alone: markers around it (a sector's ring) stay on the map, out of the frame.
    frameAreaOnly: { type: Boolean, default: false },
    // Heavy lists (national map) come from JSON instead of the markers attribute.
    markersUrl: String
  }

  connect () {
    this.googleMarkers = []
    this.clusterer = null
    this.selectedId = null
    // Google Maps and the markers JSON load when the map nears the viewport, not with the page.
    this.stopWatching = whenVisible(this.hasCanvasTarget ? this.canvasTarget : this.element, () => this.initMap())
  }

  disconnect () {
    this.stopWatching?.()
    this.connection = null
    this.clearMarkers()
    this.area?.setMap(null)
    this.area = null
    this.map = null
  }

  // An office card (data-office-id), or a card pinned without an office (data-marker-id: sector home visits).
  highlightCard (event) {
    const { markerId, officeId } = event.currentTarget.dataset
    if (markerId) {
      this.selectMarker(markerId)
    } else {
      this.selectMarkerByOfficeId(officeId)
    }
  }

  unhighlightCard () {
    this.selectMarker(null)
  }

  closeCard (event) {
    event.preventDefault()
    this.selectMarker(null)
  }

  async initMap () {
    if (Number.isNaN(this.latValue) || Number.isNaN(this.lngValue)) return

    const connection = (this.connection = Symbol("connection"))
    try {
      const [maps, markers, clusterer] = await Promise.all([
        loadGoogleMaps(),
        this.loadMarkers(),
        this.clusterValue ? import("@googlemaps/markerclusterer") : null
      ])
      if (this.connection !== connection) return

      if (clusterer) MarkerClusterer = clusterer.MarkerClusterer
      this.markers = markers
      this.icons = {
        default: pinIcon(maps),
        active: pinIcon(maps, { active: true }),
        // Zone page: markers carrying a practice mode take its breakdown color.
        offices: pinIcon(maps, { practiceMode: "offices" }),
        home_care: pinIcon(maps, { practiceMode: "home_care" }),
        both: pinIcon(maps, { practiceMode: "both" })
      }
      const center = { lat: this.latValue, lng: this.lngValue }

      this.map = new maps.Map(this.canvasTarget, {
        center,
        zoom: this.zoomValue,
        styles: this.themeValue === "light" ? MapStyleLight : MapStyle,
        maxZoom: 19,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: maps.ControlPosition.LEFT_TOP
        }
      })

      if (this.hasRadiusValue && this.radiusValue > 0) {
        this.circle = new maps.Circle({
          map: this.map,
          center,
          radius: this.radiusValue * 1000,
          strokeWeight: 0,
          fillColor: "#4988A3",
          fillOpacity: 0.1
        })
      }

      this.renderArea(maps)
      this.renderMarkers(maps)
    } catch (_error) {
      // Keep the empty map container if the script fails to load.
    }
  }

  async loadMarkers () {
    if (!this.markersUrlValue) return this.markersValue

    try {
      const response = await fetch(this.markersUrlValue, { headers: { Accept: "application/json" } })
      return response.ok ? await response.json() : []
    } catch (_error) {
      // The map still shows, without markers.
      return []
    }
  }

  markerDataList () {
    const markers = this.markers || this.markersValue
    return Array.isArray(markers) ? markers : []
  }

  areaPath () {
    return this.areaValue
      .map((point) => ({ lat: Number(point.lat), lng: Number(point.lng) }))
      .filter((point) => !Number.isNaN(point.lat) && !Number.isNaN(point.lng))
  }

  pathCenter (path) {
    return {
      lat: path.reduce((sum, point) => sum + point.lat, 0) / path.length,
      lng: path.reduce((sum, point) => sum + point.lng, 0) / path.length
    }
  }

  renderArea (maps) {
    const path = this.areaPath()
    if (path.length < 3) return

    this.area = new maps.Polygon({
      map: this.map,
      paths: path,
      clickable: false,
      strokeColor: "#4988A3",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: "#4988A3",
      fillOpacity: 0.12
    })
  }

  renderMarkers (maps) {
    this.clearMarkers()
    const bounds = new maps.LatLngBounds()
    // The outline and the markers around it frame the map (see AREA_FIT_DISTANCE_KM).
    const areaPath = this.area ? this.areaPath() : []
    areaPath.forEach((point) => bounds.extend(point))
    const areaCenter = areaPath.length ? this.pathCenter(areaPath) : null

    this.googleMarkers = this.markerDataList().map((markerData) => {
      const position = { lat: Number(markerData.lat), lng: Number(markerData.lng) }
      const framed = !areaCenter || (!this.frameAreaOnlyValue && distanceInKm(areaCenter, position) <= AREA_FIT_DISTANCE_KM)
      if (framed) bounds.extend(position)

      const marker = new maps.Marker({
        position,
        map: this.map,
        icon: this.restingIcon(markerData),
        optimized: false
      })
      marker.markerData = markerData
      marker.addListener("click", () => this.selectMarker(markerData.id, { pan: true }))
      return marker
    })

    if (this.clusterValue && MarkerClusterer && this.googleMarkers.length >= CLUSTER_MIN_MARKERS) {
      try {
        this.clusterer = new MarkerClusterer({
          map: this.map,
          markers: this.googleMarkers,
          renderer: {
            render: ({ count, position, markers }) => new maps.Marker({
              position,
              ...clusterIcon(maps, count, { practiceModes: this.practiceModeCounts(markers) }),
              zIndex: Number(maps.Marker.MAX_ZINDEX) + count
            })
          }
        })
      } catch (_error) {
        // Markers stay on the map when clustering cannot start.
      }
    }

    if (this.frameRadiusValue > 0) {
      this.map.fitBounds(this.frameBounds(maps))
    } else if (this.fitBoundsValue && (this.googleMarkers.length || areaPath.length)) {
      this.map.fitBounds(bounds)
      if (this.fitBoundsZoomOffsetValue) {
        maps.event.addListenerOnce(this.map, "idle", () => {
          this.map?.setZoom(this.map.getZoom() + this.fitBoundsZoomOffsetValue)
        })
      }
    }
  }

  // The square of frameRadius km around the centre.
  frameBounds (maps) {
    const latDelta = this.frameRadiusValue / 110.574
    const lngDelta = this.frameRadiusValue / (111.32 * Math.cos(this.latValue * Math.PI / 180))
    const bounds = new maps.LatLngBounds()
    bounds.extend({ lat: this.latValue - latDelta, lng: this.lngValue - lngDelta })
    bounds.extend({ lat: this.latValue + latDelta, lng: this.lngValue + lngDelta })
    return bounds
  }

  // null when the markers carry no practice mode (plain cluster disc).
  practiceModeCounts (markers = []) {
    const modes = markers.map((marker) => marker.markerData?.practiceMode).filter(Boolean)
    if (!modes.length) return null

    return modes.reduce((counts, mode) => ({ ...counts, [mode]: (counts[mode] || 0) + 1 }), {})
  }

  restingIcon (markerData) {
    return this.icons[markerData?.practiceMode] || this.icons.default
  }

  selectMarkerByOfficeId (officeId) {
    const markerData = this.markerDataList().find((marker) => String(marker.officeId) === String(officeId))
    this.selectMarker(markerData?.id)
  }

  selectMarker (id, { pan } = {}) {
    this.selectedId = id || null

    this.googleMarkers.forEach((marker) => {
      const selected = marker.markerData.id === this.selectedId
      marker.setIcon(selected ? this.icons.active : this.restingIcon(marker.markerData))
      marker.setZIndex(selected ? 1000 : null)
    })

    this.element.querySelectorAll(RESULT_CARD_SELECTOR).forEach((card) => {
      card.classList.remove("active")
    })

    if (this.selectedId) {
      const markerData = this.markerDataList().find((marker) => marker.id === this.selectedId)
      const card = this.element.querySelector(`[data-office-id="${markerData?.officeId}"] .visitor-practitioner-result-card`)
      card?.classList.add("active")
      this.showSelectedCard(markerData)

      if (pan && markerData) {
        this.map?.panTo({ lat: Number(markerData.lat), lng: Number(markerData.lng) })
        if (this.map && this.map.getZoom() < MARKER_FOCUS_ZOOM) this.map.setZoom(MARKER_FOCUS_ZOOM)
      }
    } else {
      this.showDefaultCard()
    }
  }

  showSelectedCard (markerData) {
    if (!this.hasSelectedCardTarget || !markerData) return

    this.selectedCardTarget.hidden = false
    if (this.hasDefaultCardTarget) this.defaultCardTarget.hidden = true
    if (this.hasCloseButtonTarget) this.closeButtonTarget.hidden = false

    this.selectedCardTarget.querySelectorAll("[data-field]").forEach((node) => {
      const value = markerData[node.dataset.field]
      if (node.dataset.field === "avatarUrl" && node.tagName === "IMG") {
        node.src = value || ""
        return
      }
      if (node.tagName === "A") {
        node.href = value || "#"
        if (node.dataset.textField) node.textContent = markerData[node.dataset.textField] || ""
        return
      }
      node.textContent = value || ""
      node.hidden = !value
    })
  }

  showDefaultCard () {
    if (this.hasSelectedCardTarget) this.selectedCardTarget.hidden = true
    if (this.hasDefaultCardTarget) this.defaultCardTarget.hidden = false
    if (this.hasCloseButtonTarget) this.closeButtonTarget.hidden = true
  }

  clearMarkers () {
    this.clusterer?.clearMarkers()
    this.clusterer = null
    this.googleMarkers.forEach((marker) => marker.setMap(null))
    this.googleMarkers = []
  }
}
